import { NgIf } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { BusinessPartner, ShippingType } from '@oe-backend/types';
import {
	HeadlineComponent,
	TabGroupComponent,
	TabNavigationComponent,
} from '@zeiss/beyond-online-angular';
import { LoadingAnimationService } from 'src/app/modules/loading-animation/loading-animation.service';
import { CartStore } from 'src/app/state-management/stores/cart.store';
import { ConfigurationStore } from 'src/app/state-management/stores/configuration.store';
import { ECPStore } from 'src/app/state-management/stores/ecp.store';
import { OrderStore } from 'src/app/state-management/stores/order.store';
import { IconButtonComponent, ToastService, ToastType } from 'vis-ods-angular';
import { RoutesEnum } from '../../../routing/routes.enum';
import { SummaryRoutesEnum } from '../../../routing/summary-outlet-routes.enum';
import { StoreSelectorComponent } from '../store-selector/store-selector.component';
import { CustomerAddressComponent } from './customer-address/customer-address.component';

@Component({
	selector: 'app-address-selector',
	templateUrl: './address-selector.component.html',
	imports: [
		TranslocoModule,
		IconButtonComponent,
		NgIf,
		StoreSelectorComponent,
		CustomerAddressComponent,
		HeadlineComponent,
		TabNavigationComponent,
		TabGroupComponent,
	],
})
export class AddressSelectorComponent implements OnInit {
	private readonly router = inject(Router);
	private readonly ecpStore = inject(ECPStore);
	private readonly orderStore = inject(OrderStore);
	private readonly cartStore = inject(CartStore);
	private readonly configurationStore = inject(ConfigurationStore);
	private readonly toastService = inject(ToastService);
	private readonly translocoService = inject(TranslocoService);
	private readonly loadingAnimationService = inject(LoadingAnimationService);

	public settings = this.configurationStore.settings;
	public stores = this.ecpStore.shipToStores;
	public storesLoading = this.ecpStore.isLoading('shipToStores');
	public selectedShipToStore = this.orderStore.selectedShipToStore;
	public isB2BShipment = this.orderStore.isB2BShipment;

	public updateCartItem = false;

	constructor() {
		this.updateCartItem =
			this.router.getCurrentNavigation()?.extras.state?.['updateCartItemAfterChange'] || false;
	}

	async ngOnInit() {
		if (this.stores.length === 0) {
			await this.ecpStore.fetchShipToStores();
		}
	}

	async onStoreSelected(store: BusinessPartner) {
		await this.updateShippingAddress(store, 'B2B');
	}

	async onCustomerAddressChanged(address: BusinessPartner) {
		await this.updateShippingAddress(address, 'B2C');
	}

	async updateShippingAddress(address: BusinessPartner, shippingType: ShippingType) {
		this.orderStore.setShippingAddress(address, shippingType);
		if (this.router.url.includes(RoutesEnum.shoppingCart)) {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.shoppingCartSummary] } }]);
		} else {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.summary] } }]);
		}
		if (this.updateCartItem) {
			const selectedCartItemId = this.orderStore.selectedCartItemId();
			const order = this.orderStore.order();
			if (selectedCartItemId && Object.keys(order).length > 0) {
				this.loadingAnimationService.open();
				await this.cartStore.updateCartItem(selectedCartItemId, order);
				await this.cartStore.fetchCartItems();
				this.loadingAnimationService.close();
				this.toastService.addToast({
					variant: ToastType.success,
					headline: this.translocoService.translate(
						'shoppingCart.subcomponents.shippingAddressUpdatedToast.headlines.title',
					),
					description: this.translocoService.translate(
						'shoppingCart.subcomponents.shippingAddressUpdatedToast.texts.message',
					),
					automaticDeleteTime: 3000,
				});
			}
		}
	}

	navigateBack() {
		if (this.router.url.includes(RoutesEnum.shoppingCart)) {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.shoppingCartSummary] } }]);
		} else {
			this.router.navigate([{ outlets: { summary: [SummaryRoutesEnum.summary] } }]);
		}
	}
}
